<script setup>
import {ref, computed} from 'vue';
import {useAuthService} from '~/composables/useAuthService';
import PlayerCard from '~/components/players/PlayerCard.vue';
import UserSelectField from '~/components/form/UserSelectField.vue';
import UserCard from "~/components/users/UserCard.vue";

const props = defineProps({
  conversation: {
    type: Object,
    required: true
  },
  canEdit: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['update-participants', 'close']);

const {user} = useAuthService();
const dialog = defineModel('dialog', {type: Boolean, default: false});

// State for adding participants
const showAddDialog = ref(false);
const selectedNewParticipantIds = ref([]);

// Computed
const participants = computed(() => {
  const participantsList = props.conversation?.participants || [];

  // Sort alphabetically by firstName, then lastName
  return [...participantsList].sort((a, b) => {
    const aFirst = (a.firstName || '').toLowerCase().trim();
    const bFirst = (b.firstName || '').toLowerCase().trim();
    const aLast = (a.lastName || '').toLowerCase().trim();
    const bLast = (b.lastName || '').toLowerCase().trim();

    // Compare first names first
    const firstNameCompare = aFirst.localeCompare(bFirst);
    if (firstNameCompare !== 0) {
      return firstNameCompare;
    }
    // If first names are the same, compare last names
    return aLast.localeCompare(bLast);
  });
});

const participantCount = computed(() => {
  return participants.value.length;
});

const participantUserIds = computed(() => {
  return props.conversation?.participantUserIds || [];
});

const adminUserIds = computed(() => {
  return props.conversation?.adminUserIds || [];
});

// Get IDs of current participants to exclude from add dialog
const excludeIds = computed(() => {
  return participants.value.map(p => p.id);
});

// Check if a participant is an admin
const isAdmin = (participantId) => {
  return adminUserIds.value.includes(participantId);
};

// Check if current user can perform admin actions
const canPerformAdminActions = computed(() => {
  return props.canEdit;
});

// Check if a participant is the last admin
const isLastAdmin = (participantId) => {
  return adminUserIds.value.length === 1 && isAdmin(participantId);
};

// Check if user can remove admin status (can't remove last admin)
const canRemoveAdmin = (participantId) => {
  return !isLastAdmin(participantId);
};

// Remove participant
const removeParticipant = (participantId) => {
  const updatedIds = participantUserIds.value.filter(id => id !== participantId);
  emit('update-participants', updatedIds);
};

// Make participant admin
const makeAdmin = (participantId) => {
  const updatedAdminIds = [...adminUserIds.value, participantId];
  emit('update-admins', updatedAdminIds);
};

// Remove admin status
const removeAdmin = (participantId) => {
  const updatedAdminIds = adminUserIds.value.filter(id => id !== participantId);
  emit('update-admins', updatedAdminIds);
};

// Add new participants
const addParticipants = () => {
  if (selectedNewParticipantIds.value.length === 0) return;

  const updatedIds = [...participantUserIds.value, ...selectedNewParticipantIds.value];
  emit('update-participants', updatedIds);

  // Reset and close add dialog
  selectedNewParticipantIds.value = [];
  showAddDialog.value = false;
};

// Cancel adding participants
const cancelAdd = () => {
  selectedNewParticipantIds.value = [];
  showAddDialog.value = false;
};

// Close main dialog
const handleClose = () => {
  dialog.value = false;
  emit('close');
};
</script>

<template>
  <v-dialog
      v-model="dialog"
      max-width="600"
      scrollable
  >
    <v-card
        class="d-flex flex-column"
        :elevation="0"
        style="border:1px solid rgb(var(--v-theme-borderLight)); border-radius:14px;"
    >
      <div class="d-flex align-center justify-space-between pa-4" style="border-bottom: 1px solid rgb(var(--v-theme-borderLight));">
        <span class="text-subtitle-1 font-weight-bold">Participants ({{ participantCount }})</span>
        <div class="d-flex align-center">
          <!-- Add button for admins -->
          <v-btn
              v-if="canEdit"
              icon="mdi-plus"
              variant="text"
              size="small"
              color="primary"
              @click="showAddDialog = true"
          />
          <v-btn
              icon="mdi-close"
              variant="text"
              size="small"
              @click="handleClose"
          />
        </div>
      </div>

      <v-card-text class="pa-0">
        <!-- Empty state -->
        <div v-if="participants.length === 0" class="text-center text-medium-emphasis pa-8">
          <v-icon size="48" :color="`rgb(var(--v-theme-textSubtle))`">mdi-account-off</v-icon>
          <div class="mt-2">No participants</div>
        </div>

        <!-- Participants list -->
        <div v-else class="participants-list">
          <template
              v-for="participant in participants"
              :key="participant.id">
          <div

              class="participant-item d-flex align-center"
          >
            <!-- Player card -->
            <div class="flex-grow-1">
              <user-card
                  :user="participant"
                  size="medium"
                  :expandable="true"
              />
            </div>

            <!-- Admin badge -->
            <span
                v-if="isAdmin(participant.id)"
                class="tl-chip tl-chip--brand ml-2"
            >
              Admin
            </span>

            <!-- Actions menu (only for admins) -->
            <template v-if="canPerformAdminActions">
            <crud-confirm-dialog
                title="Remove Participant"
                message="Are you sure you want to remove this participant from the conversation?"
                confirm-text="Remove"
                @confirm="removeParticipant(participant.id)"
            >
              <template #default="{ click }">
                <v-menu>
                  <template #activator="{ props }">
                    <v-btn
                        v-bind="props"
                        icon="mdi-dots-vertical"
                        variant="text"
                        size="small"
                        class="ml-2"
                    />
                  </template>

                  <v-list density="compact">
                    <!-- Make admin / Remove admin -->
                    <v-list-item
                        v-if="!isAdmin(participant.id)"
                        @click="makeAdmin(participant.id)"
                    >
                      <template #prepend>
                        <v-icon size="small">mdi-shield-account</v-icon>
                      </template>
                      <v-list-item-title>Make Admin</v-list-item-title>
                    </v-list-item>

                    <v-list-item
                        v-else
                        :disabled="!canRemoveAdmin(participant.id)"
                        @click="removeAdmin(participant.id)"
                    >
                      <template #prepend>
                        <v-icon size="small">mdi-shield-off</v-icon>
                      </template>
                      <v-list-item-title>
                        {{ isLastAdmin(participant.id) ? 'Last Admin' : 'Remove Admin' }}
                      </v-list-item-title>
                    </v-list-item>

                    <!-- Remove participant -->
                    <v-list-item @click="click">
                      <template #prepend>
                        <v-icon color="error" size="small">mdi-account-remove</v-icon>
                      </template>
                      <v-list-item-title>Remove</v-list-item-title>
                    </v-list-item>

                  </v-list>
                </v-menu>
              </template>
            </crud-confirm-dialog>
            </template>
          </div>
          </template>
        </div>
      </v-card-text>
    </v-card>

    <!-- Add Participants Dialog -->
    <v-dialog
        v-model="showAddDialog"
        max-width="600"
        persistent
    >
      <v-card
          :elevation="0"
          style="border:1px solid rgb(var(--v-theme-borderLight)); border-radius:14px;"
      >
        <div class="d-flex align-center justify-space-between pa-4" style="border-bottom: 1px solid rgb(var(--v-theme-borderLight));">
          <span class="text-subtitle-1 font-weight-bold">Add Participants</span>
          <v-btn
              icon="mdi-close"
              variant="text"
              size="small"
              @click="cancelAdd"
          />
        </div>

        <v-card-text class="pa-4">
          <p class="text-body-2 text-medium-emphasis mb-4">
            Search and select users to add to this conversation.
          </p>

          <user-select-field
              v-model="selectedNewParticipantIds"
              label="Select Users"
              :multiple="true"
              :exclude-ids="excludeIds"
              hint="Selected users will be added to the conversation"
          />
        </v-card-text>

        <v-card-actions class="pa-4" style="border-top: 1px solid rgb(var(--v-theme-borderLight));">
          <v-spacer/>
          <v-btn
              variant="text"
              @click="cancelAdd"
          >
            Cancel
          </v-btn>
          <v-btn
              color="primary"
              variant="flat"
              :disabled="selectedNewParticipantIds.length === 0"
              @click="addParticipants"
          >
            Add {{ selectedNewParticipantIds.length }} Participant{{
              selectedNewParticipantIds.length !== 1 ? 's' : ''
            }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<style scoped>
.participants-list {
  display: flex;
  flex-direction: column;
}

.participant-item {
  border-radius: 8px;
  padding: 8px;
  transition: background-color 0.2s;
}

.participant-item:hover {
  background-color: rgb(var(--v-theme-light));
}

.participants-list .participant-item + .participant-item {
  border-top: 1px solid rgb(var(--v-theme-borderLight));
}
</style>
